import React, { createContext, useCallback, useContext, useState } from 'react';
import { ModalProps, OpenModal } from 'types/Modal';
import CreateDataset from 'views/Datasets/components/NewDataset/CreateDataset';
import EditDataset, {
    EditDatasetProps,
} from 'views/Datasets/components/Modals/EditDatasetModal';
import CredentialsModal, {
    CredentialsModalProps,
} from 'components/modal/CredentialsModal/CredentialsModal';
import CreateOrganization from 'views/Organizations/components/CreateOrganization';
import DomainModal, {
    DomainModalProps,
} from 'views/Sagemaker/components/DomainModal';
import UserProfileModal, {
    UserProfileModalProps,
} from 'views/Sagemaker/components/UserProfileModal';
import TableAccessModal, {
    TableAccessModalProps,
} from 'views/Table/components/TableAccessModal';
import RequestAccessDataset, {
    DatasetAccessProps,
} from 'views/Datasets/components/RequestAccess/DatasetAccessModal';
import RejectRequestModal, {
    RejectRequestModalProps,
} from 'views/Shares/components/modals/RejectRequestModal';
import EditItemsModal, {
    EditItemsModalProps,
} from 'views/Shares/components/modals/EditItemsModal';
import AWSResourcesModal, {
    AWSResourcesModalProps,
} from 'components/modal/AWSResourcesModal';
import DeleteDatasetModal, {
    DeleteDatasetProps,
} from 'views/Datasets/components/Modals/DeleteDatasetModal';

interface IModalContext {
    openEditDataset: OpenModal<EditDatasetProps>;
    openCredentialsDataset: OpenModal<CredentialsModalProps>;
    openNewDatasetModal: OpenModal<ModalProps>;
    openCreateOrganizationModal: OpenModal<ModalProps>;
    openEnvironmentCredentials: OpenModal<CredentialsModalProps>;
    openSageMakerDomain: OpenModal<DomainModalProps>;
    openUserProfilesModal: OpenModal<UserProfileModalProps>;
    openTableAccess: OpenModal<TableAccessModalProps>;
    openDatasetAccess: OpenModal<DatasetAccessProps>;
    openRejectRequestModal: OpenModal<RejectRequestModalProps>;
    openEditItemsShareModal: OpenModal<EditItemsModalProps>;
    openAWSResourcesModal: OpenModal<AWSResourcesModalProps>;
    openDeleteDataset: OpenModal<DeleteDatasetProps>;
}

const ModalContext = createContext<IModalContext>({} as IModalContext);

const useDefaultModalLogic = <
    EditDatasetProps,
    CredentialsModalProps,
    ModalProps,
    DomainModalProps,
    TableAccessModalProps,
    DatasetAccessProps,
    RejectRequestModalProps,
    EditItemsModalProps,
    AWSResourcesModalProps,
    DeleteDatasetProps,
>() => {
    const [newDatasetVisible, setNewDatasetVisible] = useState(false);
    const [newDatasetProps, setNewDatasetProps] = useState<ModalProps>();
    const [editDatasetVisible, setEditDatasetVisible] = useState(false);
    const [editDatasetProps, setEditDatasetProps] =
        useState<EditDatasetProps>();
    const [credentialsDatasetVisible, setCredentialsDatasetVisible] =
        useState(false);
    const [credentialsDatasetProps, setCredentialsDatasetProps] =
        useState<CredentialsModalProps>();
    const [newOrganizationVisible, setNewOrganizationVisible] = useState(false);
    const [newOrganizationProps, setNewOrganizationProps] =
        useState<ModalProps>();

    const [environmentCredentialsVisible, setEnvironmentsCredentialsVisible] =
        useState(false);
    const [environmentCredentialsProps, setEnvironmentCredentialsProps] =
        useState<CredentialsModalProps>();

    const [sagemakerDomainVisible, setSagemakerDomainVisible] = useState(false);
    const [sagemakerDomainProps, setSagemakerDomainProps] =
        useState<DomainModalProps>();

    const [userProfileVisible, setUserProfileVisible] = useState(false);
    const [userProfileProps, setUserProfileProps] =
        useState<UserProfileModalProps>();

    const [tableAccessVisible, setTableAccessVisible] = useState(false);
    const [tableAccessProps, setTableAccessProps] =
        useState<TableAccessModalProps>();

    const [datasetAccessVisible, setDatasetAccessVisible] = useState(false);
    const [datasetAccessProps, setDatasetAccessProps] =
        useState<DatasetAccessProps>();

    const [rejectRequestVisible, setRejectRequestVisible] = useState(false);
    const [rejectRequestProps, setRejectRequestProps] =
        useState<RejectRequestModalProps>();

    const [editItemsToRequestVisible, setEditItemsToRequestVisible] =
        useState(false);
    const [editItemsToRequestProps, setEditItemsToRequestProps] =
        useState<EditItemsModalProps>();

    const [awsResourcesVisible, setAwsResourcesVisible] = useState(false);
    const [awsResourcesProps, setAwsResourcesProps] =
        useState<AWSResourcesModalProps>();

    const [deleteDatasetVisible, setDeleteDatasetVisible] = useState(false);
    const [deleteDatasetProps, setDeleteDatasetProps] =
        useState<DeleteDatasetProps>();

    const openNewDatasetModal = useCallback((props?: ModalProps) => {
        setNewDatasetProps(props);
        setNewDatasetVisible(true);
    }, []);

    const openEditDataset = useCallback((props?: EditDatasetProps) => {
        setEditDatasetProps(props);
        setEditDatasetVisible(true);
    }, []);

    const openCredentialsDataset = useCallback(
        (props?: CredentialsModalProps) => {
            setCredentialsDatasetProps(props);
            setCredentialsDatasetVisible(true);
        },
        [],
    );

    const openCreateOrganizationModal = useCallback((props?: ModalProps) => {
        setNewOrganizationProps(props);
        setNewOrganizationVisible(true);
    }, []);

    const openEnvironmentCredentials = useCallback(
        (props?: CredentialsModalProps) => {
            setEnvironmentCredentialsProps(props);
            setEnvironmentsCredentialsVisible(true);
        },
        [],
    );

    const openSageMakerDomain = useCallback((props?: DomainModalProps) => {
        setSagemakerDomainProps(props);
        setSagemakerDomainVisible(true);
    }, []);

    const openUserProfilesModal = useCallback(
        (props?: UserProfileModalProps) => {
            setUserProfileProps(props);
            setUserProfileVisible(true);
        },
        [],
    );

    const openTableAccess = useCallback((props?: TableAccessModalProps) => {
        setTableAccessProps(props);
        setTableAccessVisible(true);
    }, []);

    const openDatasetAccess = useCallback((props?: DatasetAccessProps) => {
        setDatasetAccessProps(props);
        setDatasetAccessVisible(true);
    }, []);

    const openRejectRequestModal = useCallback(
        (props?: RejectRequestModalProps) => {
            setRejectRequestProps(props);
            setRejectRequestVisible(true);
        },
        [],
    );

    const openEditItemsShareModal = useCallback(
        (props?: EditItemsModalProps) => {
            setEditItemsToRequestProps(props);
            setEditItemsToRequestVisible(true);
        },
        [],
    );
    const openAWSResourcesModal = useCallback(
        (props?: AWSResourcesModalProps) => {
            setAwsResourcesProps(props);
            setAwsResourcesVisible(true);
        },
        [],
    );
    const openDeleteDataset = useCallback((props?: DeleteDatasetProps) => {
        setDeleteDatasetProps(props);
        setDeleteDatasetVisible(true);
    }, []);

    const closeNewDatasetModal = useCallback(() => {
        setNewDatasetProps(undefined);
        setNewDatasetVisible(false);
    }, []);

    const closeEditDataset = useCallback(() => {
        setEditDatasetProps(undefined);
        setEditDatasetVisible(false);
    }, []);

    const closeCredentialsDataset = useCallback(() => {
        setCredentialsDatasetProps(undefined);
        setCredentialsDatasetVisible(false);
    }, []);

    const closeNewOrganizationModal = useCallback(() => {
        setNewOrganizationProps(undefined);
        setNewOrganizationVisible(false);
    }, []);

    const closeEnvironmentCredentials = useCallback(() => {
        setEnvironmentCredentialsProps(undefined);
        setEnvironmentsCredentialsVisible(false);
    }, []);

    const closeSageMakerDomain = useCallback(() => {
        setSagemakerDomainProps(undefined);
        setSagemakerDomainVisible(false);
    }, []);

    const closeUserProfileModal = useCallback(() => {
        setUserProfileProps(undefined);
        setUserProfileVisible(false);
    }, []);

    const closeTableAccess = useCallback(() => {
        setTableAccessProps(undefined);
        setTableAccessVisible(false);
    }, []);

    const closeDatasetAccess = useCallback(() => {
        setDatasetAccessProps(undefined);
        setDatasetAccessVisible(false);
    }, []);

    const closeRejectRequestModal = useCallback(() => {
        setRejectRequestProps(undefined);
        setRejectRequestVisible(false);
    }, []);

    const closeEditItemsShareModal = useCallback(() => {
        setEditItemsToRequestProps(undefined);
        setEditItemsToRequestVisible(false);
    }, []);

    const closeAWSResourcesModal = useCallback(() => {
        setAwsResourcesProps(undefined);
        setAwsResourcesVisible(false);
    }, []);

    const closeDeleteDataset = useCallback(() => {
        setDeleteDatasetProps(undefined);
        setDeleteDatasetVisible(false);
    }, []);

    return {
        newDatasetVisible,
        newDatasetProps,
        openNewDatasetModal,
        closeNewDatasetModal,
        editDatasetVisible,
        editDatasetProps,
        openEditDataset,
        closeEditDataset,
        credentialsDatasetProps,
        credentialsDatasetVisible,
        openCredentialsDataset,
        closeCredentialsDataset,
        newOrganizationVisible,
        newOrganizationProps,
        openCreateOrganizationModal,
        closeNewOrganizationModal,
        environmentCredentialsProps,
        environmentCredentialsVisible,
        openEnvironmentCredentials,
        closeEnvironmentCredentials,
        sagemakerDomainVisible,
        sagemakerDomainProps,
        openSageMakerDomain,
        closeSageMakerDomain,
        userProfileVisible,
        userProfileProps,
        openUserProfilesModal,
        closeUserProfileModal,
        tableAccessVisible,
        tableAccessProps,
        openTableAccess,
        closeTableAccess,
        datasetAccessProps,
        datasetAccessVisible,
        openDatasetAccess,
        closeDatasetAccess,
        rejectRequestProps,
        rejectRequestVisible,
        openRejectRequestModal,
        closeRejectRequestModal,
        openEditItemsShareModal,
        closeEditItemsShareModal,
        editItemsToRequestProps,
        editItemsToRequestVisible,
        openAWSResourcesModal,
        closeAWSResourcesModal,
        awsResourcesProps,
        awsResourcesVisible,
        deleteDatasetProps,
        deleteDatasetVisible,
        openDeleteDataset,
        closeDeleteDataset,
    };
};

export const useModal = () => useContext(ModalContext);

export const ModalContextProvider = ({
    children,
}: {
    children?: React.ReactNode;
}) => {
    const {
        openNewDatasetModal,
        closeNewDatasetModal,
        newDatasetProps,
        newDatasetVisible,
        openEditDataset,
        closeEditDataset,
        editDatasetVisible,
        editDatasetProps,
        openCredentialsDataset,
        closeCredentialsDataset,
        credentialsDatasetVisible,
        credentialsDatasetProps,
        newOrganizationProps,
        newOrganizationVisible,
        openCreateOrganizationModal,
        closeNewOrganizationModal,
        environmentCredentialsProps,
        environmentCredentialsVisible,
        openEnvironmentCredentials,
        closeEnvironmentCredentials,
        sagemakerDomainVisible,
        sagemakerDomainProps,
        openSageMakerDomain,
        closeSageMakerDomain,
        userProfileVisible,
        userProfileProps,
        openUserProfilesModal,
        closeUserProfileModal,
        tableAccessVisible,
        tableAccessProps,
        openTableAccess,
        closeTableAccess,
        datasetAccessProps,
        datasetAccessVisible,
        openDatasetAccess,
        closeDatasetAccess,
        openRejectRequestModal,
        closeRejectRequestModal,
        rejectRequestProps,
        rejectRequestVisible,
        openEditItemsShareModal,
        closeEditItemsShareModal,
        editItemsToRequestProps,
        editItemsToRequestVisible,
        openAWSResourcesModal,
        closeAWSResourcesModal,
        awsResourcesProps,
        awsResourcesVisible,
        deleteDatasetProps,
        deleteDatasetVisible,
        openDeleteDataset,
        closeDeleteDataset,
    } = useDefaultModalLogic<
        EditDatasetProps,
        CredentialsModalProps,
        ModalProps,
        DomainModalProps,
        TableAccessModalProps,
        DatasetAccessProps,
        RejectRequestModalProps,
        EditItemsModalProps,
        AWSResourcesModalProps,
        DeleteDatasetProps
    >(); // Adjust the hook to accept both RequestModalProps and NewDatasetModalProps

    const modalContextValue: IModalContext = {
        openNewDatasetModal,
        openEditDataset,
        openCredentialsDataset,
        openCreateOrganizationModal,
        openEnvironmentCredentials,
        openSageMakerDomain,
        openUserProfilesModal,
        openTableAccess,
        openDatasetAccess,
        openRejectRequestModal,
        openEditItemsShareModal,
        openAWSResourcesModal,
        openDeleteDataset,
    };

    return (
        <ModalContext.Provider value={modalContextValue}>
            {newDatasetProps && (
                <CreateDataset
                    {...newDatasetProps}
                    onClose={closeNewDatasetModal}
                    visible={newDatasetVisible}
                />
            )}
            {editDatasetProps && (
                <EditDataset
                    {...editDatasetProps}
                    onClose={closeEditDataset}
                    visible={editDatasetVisible}
                />
            )}
            {credentialsDatasetProps && (
                <CredentialsModal
                    {...credentialsDatasetProps}
                    onClose={closeCredentialsDataset}
                    visible={credentialsDatasetVisible}
                />
            )}
            {newOrganizationProps && (
                <CreateOrganization
                    {...credentialsDatasetProps}
                    onClose={closeNewOrganizationModal}
                    visible={newOrganizationVisible}
                />
            )}
            {environmentCredentialsProps && (
                <CredentialsModal
                    {...environmentCredentialsProps}
                    onClose={closeEnvironmentCredentials}
                    visible={environmentCredentialsVisible}
                />
            )}
            {sagemakerDomainProps && (
                <DomainModal
                    {...sagemakerDomainProps}
                    onClose={closeSageMakerDomain}
                    visible={sagemakerDomainVisible}
                />
            )}
            {userProfileProps && (
                <UserProfileModal
                    {...userProfileProps}
                    onClose={closeUserProfileModal}
                    visible={userProfileVisible}
                />
            )}
            {tableAccessProps && (
                <TableAccessModal
                    {...tableAccessProps}
                    onClose={closeTableAccess}
                    visible={tableAccessVisible}
                />
            )}
            {datasetAccessProps && (
                <RequestAccessDataset
                    {...datasetAccessProps}
                    onClose={closeDatasetAccess}
                    visible={datasetAccessVisible}
                />
            )}
            {rejectRequestProps && (
                <RejectRequestModal
                    {...rejectRequestProps}
                    onClose={closeRejectRequestModal}
                    visible={rejectRequestVisible}
                />
            )}
            {editItemsToRequestProps && (
                <EditItemsModal
                    {...editItemsToRequestProps}
                    onClose={closeEditItemsShareModal}
                    visible={editItemsToRequestVisible}
                />
            )}
            {awsResourcesProps && (
                <AWSResourcesModal
                    {...awsResourcesProps}
                    onClose={closeAWSResourcesModal}
                    visible={awsResourcesVisible}
                />
            )}
            {deleteDatasetProps && (
                <DeleteDatasetModal
                    {...deleteDatasetProps}
                    onClose={closeDeleteDataset}
                    visible={deleteDatasetVisible}
                />
            )}
            {children}
        </ModalContext.Provider>
    );
};
